import React, { useState } from "react";
import * as finService from "../../services/finService";
import useModuleAccess from "../../hooks/useModuleAccess";
import { hasModulePermission } from "../../constants/platformModules";
import useFinOptions from "./useFinOptions";
import { fmtMoney } from "./gl/glContext";
import { Badge, DataTable, act, hint, linkBtn } from "./gl/glUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, formatDate, formatDateTime, inputStyle, pageStyle, todayYmd, useLoad } from "../../components/ui";

// Receivables (Finance): customer accounts with their credit, and the invoices and credit memos
// raised by the Dealership or entered here. Receipts, PDC and statements come with Cash & Bank.
const CLASSES = ["RETAIL", "CORPORATE", "FLEET", "GOVERNMENT", "INSURANCE", "LENDER", "OEM", "INTERNAL"].map((c) => ({ label: c.charAt(0) + c.slice(1).toLowerCase(), value: c }));
const CREDIT = { NONE: "Cash", PENDING: "PENDING_APPROVAL", APPROVED: "APPROVED", REJECTED: "REJECTED", ON_HOLD: "CLOSED" };

function useCanEdit() {
  const { access } = useModuleAccess();
  return hasModulePermission(access, "FIN", "FIN_AR", "FIN_CONTROLLER", "FIN_ACCOUNTANT");
}

/* ------------------------------ customers ------------------------------ */

function CustomerView({ id, onHide, onChanged }) {
  const canEdit = useCanEdit();
  const terms = useFinOptions(["payment-terms"]);
  const { data: c, loading, error, reload } = useLoad(() => finService.getCustomer(id), [id]);
  const trx = useLoad(() => finService.listTransactions({ customer_account_id: id, size: 100 }), [id]);
  const [form, setForm] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <SlidePanel show onHide={onHide} title={c ? `${c.name} (${c.account_number})` : "Customer"} width={980}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {c ? (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {canEdit ? <PrimaryButton onClick={() => setForm("edit")}>Edit</PrimaryButton> : null}
              {c.credit_status !== "PENDING" ? <SecondaryButton onClick={() => setForm("credit")}>Request credit</SecondaryButton> : null}
              {canEdit && c.credit_status !== "ON_HOLD" && c.credit_status === "APPROVED" ? <SecondaryButton onClick={() => setForm("hold")}>Put on hold</SecondaryButton> : null}
              {canEdit && c.credit_status === "ON_HOLD" ? <SecondaryButton onClick={async () => (await act(() => finService.setCustomerHold(id, false))) && refresh()}>Release hold</SecondaryButton> : null}
              {canEdit ? <SecondaryButton onClick={() => setForm("site")}>Add address</SecondaryButton> : null}
              {canEdit ? <SecondaryButton onClick={() => setForm("contact")}>Add contact</SecondaryButton> : null}
            </div>
            <div className="row" style={{ fontSize: 14 }}>
              <div className="col-md-6">
                <div>
                  {c.party_type === "COMPANY" ? "Company" : "Individual"} · {c.customer_class.toLowerCase()}
                </div>
                <div>{[c.phone, c.email].filter(Boolean).join(" · ")}</div>
                <div>TRN: {c.trn || "—"}</div>
              </div>
              <div className="col-md-6">
                <div>
                  Credit: <Badge status={CREDIT[c.credit_status] || c.credit_status} /> {c.credit_status === "APPROVED" ? `limit ${fmtMoney(c.credit_limit)} ${c.credit_currency || ""}` : c.is_cash ? "cash customer" : ""}
                </div>
                <div>Terms: {c.payment_term_name || "Immediate"}</div>
                <div>
                  Open balance: <b>{fmtMoney(c.open_balance)}</b>
                </div>
              </div>
            </div>
            <h6 className="mt-3">Addresses</h6>
            <DataTable rows={c.sites} minWidth={500} empty={<div style={hint}>None.</div>} columns={[{ label: "Site", render: (s) => s.site_number }, { label: "Purpose", render: (s) => s.purpose }, { label: "Address", render: (s) => [s.address, s.city, s.emirate_state].filter(Boolean).join(", ") || "—" }, { label: "", render: (s) => (s.is_primary ? "Primary" : "") }]} />
            <h6 className="mt-3">Contacts</h6>
            <DataTable rows={c.contacts} minWidth={500} empty={<div style={hint}>None.</div>} columns={[{ label: "Name", render: (x) => x.name }, { label: "Role", render: (x) => x.role }, { label: "Phone", render: (x) => x.phone }, { label: "Email", render: (x) => x.email }]} />
            <h6 className="mt-3">Transactions</h6>
            <DataTable
              rows={trx.data || []}
              minWidth={640}
              empty={<div style={hint}>None.</div>}
              columns={[
                { label: "Number", render: (t) => <button type="button" style={linkBtn} onClick={() => finService.transactionPdf(t.id, `${t.trx_number}.pdf`)}>{t.trx_number}</button> },
                { label: "Date", render: (t) => formatDate(t.trx_date) },
                { label: "Type", render: (t) => (t.trx_class === "CM" ? "Credit memo" : "Invoice") },
                { label: "Total", num: true, render: (t) => fmtMoney(t.total) },
                { label: "Due", num: true, render: (t) => fmtMoney(t.amount_due_remaining) },
              ]}
            />
            <h6 className="mt-3">Credit history</h6>
            {c.credit_history.length ? (
              c.credit_history.map((h, i) => (
                <div key={i} style={{ fontSize: 13 }}>
                  {formatDateTime(h.created_at)} · {h.action.toLowerCase()} {h.new_limit !== null ? `· limit ${fmtMoney(h.new_limit)}` : ""} {h.comment ? `· ${h.comment}` : ""}
                </div>
              ))
            ) : (
              <div style={hint}>None.</div>
            )}
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={form === "edit"}
        title="Edit customer"
        initial={c ? { name: c.name, trn: c.trn, phone: c.phone, email: c.email, customer_class: c.customer_class } : {}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => finService.saveCustomer(id, clean)}
        fields={[
          { key: "name", label: "Name", required: true, max: 200 },
          { key: "customer_class", label: "Class", type: "select", required: true, options: CLASSES },
          { key: "trn", label: "Tax number (TRN)", max: 32 },
          { key: "phone", label: "Phone", max: 30 },
          { key: "email", label: "Email", max: 150 },
        ]}
      />
      <FormDrawer
        show={form === "credit"}
        title="Request credit"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => finService.requestCredit(id, { ...clean, credit_limit: Number(clean.credit_limit) })}
        fields={[
          { key: "credit_limit", label: "Credit limit", type: "number", step: "any", min: 0, required: true },
          { key: "payment_term_id", label: "Payment terms", type: "select", options: terms["payment-terms"] || [] },
          { key: "comment", label: "Why", type: "textarea", max: 500 },
        ]}
      >
        <div style={hint}>Finance approves the limit (Approvals). Until then the customer pays before delivery.</div>
      </FormDrawer>
      <FormDrawer show={form === "hold"} title="Put on credit hold" initial={{}} onHide={() => setForm(null)} onDone={refresh} onSave={(clean) => finService.setCustomerHold(id, true, clean.comment)} fields={[{ key: "comment", label: "Reason", type: "textarea", required: true, max: 500 }]} />
      <FormDrawer
        show={form === "site"}
        title="Add address"
        initial={{ purpose: "BILL_TO", country_code: "AE" }}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => finService.saveCustomerSite(id, null, clean)}
        fields={[
          { key: "purpose", label: "Purpose", type: "select", required: true, options: [{ label: "Bill to", value: "BILL_TO" }, { label: "Ship to", value: "SHIP_TO" }] },
          { key: "address", label: "Address", type: "textarea", max: 500 },
          { key: "city", label: "City", max: 100 },
          { key: "emirate_state", label: "Emirate / state", max: 60 },
          { key: "country_code", label: "Country code", max: 2 },
        ]}
      />
      <FormDrawer
        show={form === "contact"}
        title="Add contact"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => finService.saveCustomerContact(id, null, clean)}
        fields={[
          { key: "name", label: "Name", required: true, max: 150 },
          { key: "role", label: "Role", max: 60 },
          { key: "phone", label: "Phone", max: 30 },
          { key: "email", label: "Email", max: 150 },
        ]}
      />
    </SlidePanel>
  );
}

function Customers() {
  const canEdit = useCanEdit();
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => finService.listCustomers({ q: q || undefined }), [q]);
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {canEdit ? <PrimaryButton onClick={() => setAdding(true)}>Add customer</PrimaryButton> : null}
        <input className="form-control" style={{ ...inputStyle, width: 260 }} placeholder="Name, account, phone or TRN" aria-label="Search customers" onKeyDown={(e) => e.key === "Enter" && setQ(e.target.value.trim())} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No customers" text="Customers are created when a quotation is won, or here." />}
          columns={[
            { label: "Account", render: (r) => <b>{r.account_number}</b> },
            { label: "Name", render: (r) => r.name },
            { label: "Class", render: (r) => r.customer_class.toLowerCase() },
            { label: "Phone", render: (r) => r.phone },
            { label: "Credit", render: (r) => (r.credit_status === "NONE" ? "Cash" : <Badge status={CREDIT[r.credit_status] || r.credit_status} />) },
            { label: "Limit", num: true, render: (r) => (Number(r.credit_limit) ? fmtMoney(r.credit_limit) : "—") },
            { label: "Open balance", num: true, render: (r) => fmtMoney(r.open_balance) },
          ]}
        />
      </LoadState>
      {open ? <CustomerView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={adding}
        title="Add customer"
        initial={{ party_type: "COMPANY", customer_class: "CORPORATE" }}
        onHide={() => setAdding(false)}
        onDone={reload}
        onSave={(clean) => finService.saveCustomer(null, clean)}
        fields={(v) => [
          { key: "party_type", label: "Type", type: "select", required: true, options: [{ label: "Company", value: "COMPANY" }, { label: "Individual", value: "INDIVIDUAL" }] },
          ...(v.party_type === "COMPANY" ? [{ key: "name", label: "Company name", required: true, max: 200 }] : [{ key: "first_name", label: "First name", required: true, max: 45 }, { key: "last_name", label: "Last name", max: 45 }]),
          { key: "customer_class", label: "Class", type: "select", required: true, options: CLASSES },
          { key: "phone", label: "Phone", required: v.party_type !== "COMPANY", max: 30 },
          { key: "email", label: "Email", max: 150 },
          { key: "trn", label: "Tax number (TRN)", max: 32 },
          { key: "address", label: "Address", type: "textarea", max: 500 },
          { key: "emirate_state", label: "Emirate / state", max: 60 },
        ]}
      />
    </>
  );
}

/* ------------------------------ transactions ------------------------------ */

function TransactionView({ id, onHide }) {
  const { data: t, loading, error, reload } = useLoad(() => finService.getTransaction(id), [id]);
  return (
    <SlidePanel show onHide={onHide} title={t ? `${t.trx_class === "CM" ? "Credit memo" : "Invoice"} ${t.trx_number}` : "Transaction"} width={900}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {t ? (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <PrimaryButton onClick={() => finService.transactionPdf(id, `${t.trx_number}.pdf`)}>{t.trx_class === "CM" ? "Tax credit note PDF" : "Tax invoice PDF"}</PrimaryButton>
            </div>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              {t.customer_name} ({t.account_number}) · {formatDate(t.trx_date)} · due {formatDate(t.due_date)} · {t.source_doc_number ? `from ${t.source_doc_number}` : "entered in Finance"}
              {t.related_trx_number ? ` · credits ${t.related_trx_number}` : ""}
              <div style={hint}>Accounting: {t.accounting_status ? t.accounting_status.toLowerCase() : "—"}{t.accounting_error ? ` (${t.accounting_error})` : ""}</div>
            </div>
            <DataTable
              rows={t.lines}
              rowKey={(l) => l.line_number}
              minWidth={760}
              columns={[
                { label: "#", render: (l) => l.line_number },
                { label: "Description", render: (l) => l.description },
                { label: "Qty", num: true, render: (l) => Number(l.quantity) },
                { label: "Price", num: true, render: (l) => fmtMoney(l.unit_price) },
                { label: "Discount", num: true, render: (l) => (Number(l.discount_amount) ? fmtMoney(l.discount_amount) : "—") },
                { label: "Amount", num: true, render: (l) => fmtMoney(l.amount) },
                { label: "VAT", num: true, render: (l) => `${fmtMoney(l.tax_amount)} (${Number(l.tax_rate_pct)}%)` },
              ]}
            />
            <div style={{ textAlign: "right", fontSize: 14, marginTop: 8 }}>
              VAT {fmtMoney(t.tax_total)} · <b>Total {t.currency} {fmtMoney(t.total)}</b> · applied {fmtMoney(t.applied_total)} · <b>due {fmtMoney(t.amount_due_remaining)}</b>
            </div>
            {t.applications.length ? (
              <>
                <h6 className="mt-3">Applied</h6>
                {t.applications.map((a, i) => (
                  <div key={i} style={{ fontSize: 13 }}>
                    {formatDate(a.apply_date)} · {a.app_type.toLowerCase().replace(/_/g, " ")} {a.source_reference || ""} · {fmtMoney(a.amount)}
                  </div>
                ))}
              </>
            ) : null}
            {t.schedules.length > 1 ? (
              <>
                <h6 className="mt-3">Instalments</h6>
                {t.schedules.map((s) => (
                  <div key={s.installment_no} style={{ fontSize: 13 }}>
                    {s.installment_no}. {formatDate(s.due_date)} · {fmtMoney(s.amount_due_original)} · due {fmtMoney(s.amount_due_remaining)}
                  </div>
                ))}
              </>
            ) : null}
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

function NewTransaction({ onHide, onDone }) {
  const customers = useLoad(() => finService.listCustomers({ size: 200 }), []);
  const fin = useFinOptions(["business-units", "tax-rates", "currencies"]);
  const lineTypes = useLoad(() => finService.listArLineTypes(), []);
  const openInv = useLoad(() => finService.listTransactions({ open: 1, trx_class: "INV", size: 200 }), []);
  const [h, setH] = useState({ trx_class: "INV", trx_date: todayYmd(), currency: "AED", customer_account_id: null, business_unit_id: null, related_trx_id: null, description: "" });
  const [lines, setLines] = useState([{ line_type: "OTHER_REVENUE", description: "", quantity: 1, unit_price: "", discount_amount: "", tax_rate_code: "AE_STD" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const save = async () => {
    setBusy(true);
    const res = await act(() => finService.createTransaction({ ...h, lines: lines.map((l) => ({ ...l, quantity: Number(l.quantity || 1), unit_price: Number(l.unit_price || 0), discount_amount: Number(l.discount_amount || 0) })) }));
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel
      show
      onHide={() => !busy && onHide()}
      title={h.trx_class === "CM" ? "New credit memo" : "New invoice"}
      width={980}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy}>
            Create
          </PrimaryButton>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Type" htmlFor="t-cls">
          <select id="t-cls" className="form-select" value={h.trx_class} onChange={(e) => setH({ ...h, trx_class: e.target.value })}>
            <option value="INV">Invoice</option>
            <option value="DM">Debit memo</option>
            <option value="CM">Credit memo</option>
          </select>
        </Field>
        <Field label="Customer" required htmlFor="t-cust">
          <AppDropdown id="t-cust" value={h.customer_account_id} filter options={(customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id }))} onChange={(e) => setH({ ...h, customer_account_id: e.value })} />
        </Field>
        {h.trx_class === "CM" ? (
          <Field label="Credits invoice" required htmlFor="t-rel">
            <AppDropdown id="t-rel" value={h.related_trx_id} filter options={(openInv.data || []).map((t) => ({ label: `${t.trx_number} · ${t.customer_name} · ${fmtMoney(t.amount_due_remaining)}`, value: t.id }))} onChange={(e) => setH({ ...h, related_trx_id: e.value })} />
          </Field>
        ) : null}
        <Field label="Business unit" htmlFor="t-bu">
          <AppDropdown id="t-bu" value={h.business_unit_id} options={fin["business-units"] || []} onChange={(e) => setH({ ...h, business_unit_id: e.value })} placeholder="Default legal entity" />
        </Field>
        <Field label="Date" htmlFor="t-date">
          <input id="t-date" type="date" className="form-control" style={inputStyle} value={h.trx_date} onChange={(e) => setH({ ...h, trx_date: e.target.value })} />
        </Field>
        <Field label="Currency" htmlFor="t-ccy">
          <AppDropdown id="t-ccy" value={h.currency} options={(fin.currencies || []).map((o) => ({ label: o.label, value: o.code }))} onChange={(e) => setH({ ...h, currency: e.value })} />
        </Field>
      </div>
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-end mb-2">
          <div style={{ width: 200 }}>
            <AppDropdown id={`lt-${i}`} value={l.line_type} options={(lineTypes.data || []).map((x) => ({ label: x.toLowerCase().replace(/_/g, " "), value: x }))} onChange={(e) => set(i, { line_type: e.value })} />
          </div>
          <input className="form-control" style={{ ...inputStyle, flex: 1, minWidth: 200 }} placeholder="Description" aria-label="Description" value={l.description} onChange={(e) => set(i, { description: e.target.value })} />
          <input type="number" className="form-control" style={{ ...inputStyle, width: 80 }} aria-label="Quantity" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          <input type="number" className="form-control" style={{ ...inputStyle, width: 120 }} placeholder="Price" aria-label="Price" value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} />
          <input type="number" className="form-control" style={{ ...inputStyle, width: 110 }} placeholder="Discount" aria-label="Discount" value={l.discount_amount} onChange={(e) => set(i, { discount_amount: e.target.value })} />
          <div style={{ width: 160 }}>
            <AppDropdown id={`tx-${i}`} value={l.tax_rate_code} options={(fin["tax-rates"] || []).map((o) => ({ label: o.label, value: o.code }))} onChange={(e) => set(i, { tax_rate_code: e.value })} />
          </div>
          {lines.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines([...lines, { line_type: "OTHER_REVENUE", description: "", quantity: 1, unit_price: "", discount_amount: "", tax_rate_code: "AE_STD" }])}>
        Add line
      </button>
      <Field label="Description" htmlFor="t-desc">
        <textarea id="t-desc" className="form-control" rows={2} value={h.description} onChange={(e) => setH({ ...h, description: e.target.value })} />
      </Field>
    </SlidePanel>
  );
}

function Transactions() {
  const canEdit = useCanEdit();
  const [f, setF] = useState({ trx_class: "", open: false, q: "" });
  const { data, loading, error, reload } = useLoad(() => finService.listTransactions({ trx_class: f.trx_class || undefined, open: f.open ? 1 : undefined, q: f.q || undefined }), [f.trx_class, f.open, f.q]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {canEdit ? <PrimaryButton onClick={() => setCreating(true)}>New invoice / credit memo</PrimaryButton> : null}
        <div style={{ width: 170 }}>
          <AppDropdown
            id="tr-cls"
            value={f.trx_class}
            options={[
              { label: "All types", value: "" },
              { label: "Invoices", value: "INV" },
              { label: "Credit memos", value: "CM" },
              { label: "Debit memos", value: "DM" },
            ]}
            onChange={(e) => setF({ ...f, trx_class: e.value })}
          />
        </div>
        <label style={{ fontSize: 14 }}>
          <input type="checkbox" checked={f.open} onChange={(e) => setF({ ...f, open: e.target.checked })} /> Open only
        </label>
        <input className="form-control" style={{ ...inputStyle, width: 240 }} placeholder="Number, customer or order" aria-label="Search transactions" onKeyDown={(e) => e.key === "Enter" && setF({ ...f, q: e.target.value.trim() })} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No transactions" />}
          columns={[
            { label: "Number", render: (r) => <b>{r.trx_number}</b> },
            { label: "Date", render: (r) => formatDate(r.trx_date) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Type", render: (r) => r.trx_type_code.toLowerCase().replace(/_/g, " ") },
            { label: "Source", render: (r) => r.source_doc_number || r.source_app },
            { label: "Total", num: true, render: (r) => `${fmtMoney(r.total)} ${r.currency}` },
            { label: "Due", num: true, render: (r) => fmtMoney(r.amount_due_remaining) },
            { label: "Due date", render: (r) => formatDate(r.due_date) },
          ]}
        />
      </LoadState>
      {open ? <TransactionView id={open} onHide={() => setOpen(null)} /> : null}
      {creating ? <NewTransaction onHide={() => setCreating(false)} onDone={reload} /> : null}
    </>
  );
}

export default function Receivables() {
  const [tab, setTab] = useState("customers");
  return (
    <div style={pageStyle}>
      <PageHeader title="Receivables" subtitle="Customers, credit, invoices and credit memos" />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: "customers", label: "Customers" },
          { key: "transactions", label: "Invoices and credit memos" },
        ]}
      />
      {tab === "customers" ? <Customers /> : <Transactions />}
    </div>
  );
}
